import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Target, Eye, Award, Factory, Users } from 'lucide-react';
import { mission, vision, timeline, stats } from '../mock';

// Key phrases per year — wrapped in red bold for visual highlight
const HIGHLIGHTS = {
  "1927": ["Humble beginnings", "Company founded"],
  "1963": ["M/s Motokov-FTC", "Czechoslovakia"],
  "1983": ["500 crash fire tenders", "Tatra"],
  "1988": ["Smoke & Fire Detection Technology", "Sprinkler Suppression System"],
  "1994": ["Archana Airways", "First scheduled airlines"],
  "2004": ["Ghibellines Security Solutions"],
  "2007": ["Bhartiya Cryogas"],
  "2008": ["Rosenbauer International"],
  "2010": ["Golden Jubilee", "Indian Air Force"],
  "2020": ["250+ Rosenbauer ARFFV"],
  "2022": ["Bharat Panther", "Made in India"],
  "2027": ["100 Years of Excellence"],
};

const renderTimelineEvent = (year, text) => {
  const terms = HIGHLIGHTS[year] || [];
  if (!terms.length) return text;
  // Build a regex matching any of the highlight phrases
  const pattern = new RegExp(`(${terms.map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`, 'g');
  const parts = text.split(pattern);
  return parts.map((part, i) =>
    terms.includes(part) ? (
      <span key={i} className="font-semibold text-red-600">{part}</span>
    ) : (
      <React.Fragment key={i}>{part}</React.Fragment>
    )
  );
};

const About = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-slate-900 text-white py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0 opacity-[0.08]" style={{
          backgroundImage: 'radial-gradient(circle at 20% 30%, white 1px, transparent 1px), radial-gradient(circle at 80% 70%, white 1px, transparent 1px)',
          backgroundSize: '48px 48px'
        }}></div>
        <div className="absolute left-0 top-0 h-1 w-full bg-red-600"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <span className="eyebrow" style={{ color: '#f4a8b4' }}>About Us</span>
            <h1 className="font-display text-4xl md:text-6xl font-extrabold mb-5 mt-3 text-balance">Nearly a Century of Excellence</h1>
            <p className="text-base md:text-lg text-slate-300">
              Approaching a Century as India&apos;s most trusted name in fire and safety engineering.
            </p>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-24 bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-14">
            <span className="eyebrow">Purpose</span>
            <h2 className="font-display text-3xl md:text-5xl font-extrabold text-slate-900 mt-3 mb-5 text-balance">Vision & Mission</h2>
            <div className="w-16 h-[3px] bg-red-600 mx-auto"></div>
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <Card className="border border-slate-200 shadow-none rounded-md hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
              <CardContent className="p-10">
                <div className="flex items-center gap-4 mb-5">
                  <div className="w-12 h-12 bg-red-600/10 rounded-md flex items-center justify-center">
                    <Eye className="h-6 w-6 text-red-600" />
                  </div>
                  <h3 className="font-display text-2xl font-bold text-slate-900">Our Vision</h3>
                </div>
                <p className="text-slate-600 leading-relaxed text-base">{vision}</p>
              </CardContent>
            </Card>

            <Card className="border border-slate-200 shadow-none rounded-md hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
              <CardContent className="p-10">
                <div className="flex items-center gap-4 mb-5">
                  <div className="w-12 h-12 bg-red-600/10 rounded-md flex items-center justify-center">
                    <Target className="h-6 w-6 text-red-600" />
                  </div>
                  <h3 className="font-display text-2xl font-bold text-slate-900">Our Mission</h3>
                </div>
                <p className="text-slate-600 leading-relaxed text-base">{mission}</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Our Story Section — full-width dark charcoal with red accents */}
      <section className="py-24 text-white relative overflow-hidden" style={{ backgroundColor: '#1F1F1F' }}>
        <div className="absolute inset-0 opacity-[0.05]" style={{
          backgroundImage: 'radial-gradient(circle at 20% 30%, white 1px, transparent 1px), radial-gradient(circle at 80% 70%, white 1px, transparent 1px)',
          backgroundSize: '48px 48px'
        }}></div>
        <div className="absolute left-0 top-0 h-1 w-full bg-red-600"></div>
        <div className="relative w-full px-6 sm:px-10 lg:px-20 xl:px-28">
          <div className="text-center mb-14">
            <span className="eyebrow" style={{ color: '#f4a8b4' }}>Our Story</span>
            <h2 className="font-display text-3xl md:text-5xl font-extrabold text-white mt-3 mb-5 text-balance">A Legacy of Excellence</h2>
            <div className="w-16 h-[3px] bg-red-600 mx-auto"></div>
          </div>

          <div className="space-y-8 text-slate-200 leading-[1.85] text-base md:text-lg max-w-[1500px] mx-auto">
            <p>
              Founded in <span className="font-semibold text-red-500">1927</span>, the Bhartiya Group is India&apos;s most established names in fire safety, emergency response, and specialized engineering solutions. <span className="font-semibold text-red-500">Approaching a Century</span> of excellence, the Group has built a strong legacy of serving the <span className="font-semibold text-red-500">Government of India</span> and its strategic institutions, including the <span className="font-semibold text-white">Ministry of Defence, Indian Air Force, Indian Navy, Indian Coast Guard, Airports Authority of India, Ministry of Civil Aviation</span>, and various State Governments. Through its commitment to quality, innovation, and long-term partnerships, Bhartiya Group has successfully executed high-value projects exceeding <span className="font-semibold text-red-500">USD 300 million</span> and today employs over <span className="font-semibold text-red-500">500 skilled professionals</span>, veterans, engineers, and technicians.
            </p>
            <p>
              The Group has consistently pioneered advancements in fire and safety technologies in India. In the <span className="font-semibold text-red-500">1980s</span>, it introduced modern <span className="font-semibold text-red-500">smoke and fire detection systems, fire hydrant networks, and sprinkler-based suppression solutions</span>, helping transform fire protection standards across the country. Bhartiya Group has maintained strategic collaborations with leading global manufacturers, including <span className="font-semibold text-red-500">Rosenbauer, Tatra, THT, Sancakli Itfaiye, and Twin Disc</span>. Its partnership with Rosenbauer, the global leader in firefighting technology, has enabled the successful deployment and lifecycle support of <span className="font-semibold text-red-500">hundreds of advanced fire vehicles in India</span>. The Group is also recognized as the <span className="font-semibold text-red-500">first company in India</span> to undertake major overhauls of firefighting vehicles and provide comprehensive annual maintenance services for critical emergency response fleets.
            </p>
            <p>
              Today, Bhartiya Group operates a state-of-the-art manufacturing facility spread across more than <span className="font-semibold text-red-500">215,000 square feet in Sahibabad, Ghaziabad</span>, where it manufactures the <span className="font-semibold text-red-500">BHARAT PANTHER</span>, India&apos;s first and most advanced <span className="font-semibold text-red-500">Airfield Rescue and Fire Fighting Vehicles (ARFFVs)</span>, supporting the Government&apos;s <span className="font-semibold text-red-500">Make in India</span> initiative. As a comprehensive fire and safety solutions provider, the Group delivers a wide portfolio of products and services, including <span className="font-semibold text-white">airfield crash fire tenders, foam and water tenders, quick response vehicles, fire detection and suppression systems, ultra-high-pressure firefighting pumps, rescue equipment, and personal protective gear</span>. With a heritage built on innovation, reliability, and engineering excellence, Bhartiya Group continues to set benchmarks in <span className="font-semibold text-red-500">safeguarding critical infrastructure and communities across India and beyond</span>.
            </p>
          </div>
        </div>
      </section>

      {/* Timeline — horizontal 3-row × 4-col grid with connecting lines */}
      <section className="py-24 bg-slate-100 border-y border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="eyebrow">Our Journey</span>
            <h2 className="font-display text-3xl md:text-5xl font-extrabold text-slate-900 mt-3 mb-5 text-balance">Milestones Through the Decades</h2>
            <p className="text-base md:text-lg text-slate-600 max-w-2xl mx-auto">
              Defining moments that shaped our legacy in fire and safety engineering.
            </p>
          </div>

          <div className="space-y-14 md:space-y-20">
            {[0, 1, 2].map((rowIdx) => {
              const rowItems = timeline.slice(rowIdx * 4, rowIdx * 4 + 4);
              return (
                <div key={rowIdx} className="relative">
                  {/* Connecting horizontal line behind nodes (desktop) */}
                  <div className="hidden md:block absolute left-[12.5%] right-[12.5%] top-[28px] h-[2px] bg-gradient-to-r from-red-200 via-red-400 to-red-200"></div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6 md:gap-4 relative">
                    {rowItems.map((item, i) => {
                      const isDark = ['1983', '1994', '2020', '2022'].includes(item.year);
                      return (
                        <div key={i} className="flex flex-col items-center text-center">
                          {/* Node */}
                          <div className="relative z-10 flex flex-col items-center">
                            <div className={`w-14 h-14 rounded-full text-white font-display font-extrabold text-sm flex items-center justify-center shadow-lg ring-4 ring-white ${isDark ? 'bg-slate-900' : 'bg-red-600'}`}>
                              {item.year}
                            </div>
                          </div>
                          {/* Card */}
                          <div className="mt-6 w-full bg-white border border-slate-200 rounded-md p-5 shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300">
                            <p className="text-sm text-slate-700 leading-relaxed">
                              {renderTimelineEvent(item.year, item.event)}
                            </p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Company Highlights */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">Company Highlights</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-none shadow-lg">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Factory className="h-8 w-8 text-red-600" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">World-Class Manufacturing</h3>
                <p className="text-slate-600 mb-4">
                  State-of-the-art facility spanning over 20,000 sq.m in Sahibabad Industrial Area with advanced 3D modeling and computer programming capabilities
                </p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Award className="h-8 w-8 text-red-600" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">Quality Certifications</h3>
                <p className="text-slate-600 mb-4">
                  ISO 9001:2015 certified with adherence to international standards including ICAO, NFPA, and various Indian Standards
                </p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-lg">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="h-8 w-8 text-red-600" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">Expert Team</h3>
                <p className="text-slate-600 mb-4">
                  Over 500 skilled professionals including experienced business experts, trained service engineers, and technicians
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-gradient-to-r from-red-600 to-red-700 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl md:text-5xl font-bold mb-2">{stat.value}</div>
                <div className="text-base md:text-lg text-red-100">{stat.label}</div>
                {stat.subtitle && (
                  <div className="text-xs text-red-100 mt-1 italic opacity-90">{stat.subtitle}</div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;